// Imports
import { SupportAgent } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { DataGrid, GridColumns } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GobikesApi } from '../../config/Apis';
import { NavTabIndices } from '../../config/Constants';
import { getHubSelection } from '../../features/hubSelection';
import { setNavSelection } from '../../features/navSelection';
import { getAuthCookie } from '../auth/AuthUtil';
import { parseErrorForAxios, toIstDateString } from '../utils/CommonUtils';

// Types and constants
interface TicketModel {
  id: number;
  orderId: number;
  category: string;
  subReason?: string | null;
  agentRemark?: string | null;
  priority: string;
  status: string;
  createdAt: string;
  createdBy?: { id: number; name: string };
  order?: {
    user?: { firstName: string; lastName: string; phone: string; email: string };
  };
}
interface TicketEventModel {
  id: number;
  type: string;
  text: string;
  createdAt: string;
  admin?: { id: number; name: string };
}
const PAGE_SIZE = 20;
const PRIORITY_OPTIONS = [
  { label: 'Urgent', value: 'urgent' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' }
];
const STATUS_OPTIONS = [
  { label: 'New', value: 'new' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Resolved', value: 'resolved' }
];
const CATEGORY_LABELS: { [k: string]: string } = {
  deposit_refund_issue: 'Deposit Refund Issue',
  vehicle_breakdown_mechanical_issue: 'Vehicle Breakdown / Emergency',
  dealer_dispute: 'Dealer Dispute & Unavailability',
  booking_app_assistance: 'Booking & App Assistance',
  accident_emergency: 'Accident / Emergency',
  vehicle_quality_condition_at_pickup: 'Vehicle Quality & Docs at Pickup',
  trip_extension_drop_timings: 'Trip Extension',
  overtime_extra_km_fee_disputes: 'Overtime & Extra Km Disputes',
  payment_failed_refund_requests: 'Payment Failed & Refund Requests',
  challan_tolls_traffic_fines: 'Challan & Traffic Fines',
  booking_cancellation: 'Booking Cancellation',
  reschedule_booking: 'Reschedule Booking',
  kyc_account_help: 'KYC & Account Help',
  other: 'Other'
};
const EVENT_TYPE_LABELS: { [k: string]: string } = {
  customer_note: 'Customer Note',
  agent_note: 'Agent Note',
  status_change: 'Status Change'
};
const formatOption = (options: { label: string; value: string }[], value: string) =>
  options.find((o) => o.value === value)?.label || value;
const formatDate = (value: string) => toIstDateString(new Date(value).getTime());

// Main component
export function TicketDashboard(props: {
  window?: () => Window;
  drawerWidth: number
}) {
  // Local state
  const [tickets, setTickets] = useState<TicketModel[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [detailTicketId, setDetailTicketId] = useState<number | null>(null);
  const [detail, setDetail] = useState<{ ticket: TicketModel; events: TicketEventModel[] } | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);

  // Global state
  const selectedHub = useSelector(getHubSelection);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up('md'));

  // Set nav state
  useEffect(() => {
    dispatch(setNavSelection(NavTabIndices.TICKETS));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset to first page when hub changes
  useEffect(() => {
    setPage(0);
  }, [selectedHub]);

  // Load tickets
  useEffect(() => {
    const authCookie = getAuthCookie();
    if (!authCookie || !selectedHub.id) return;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const { data } = await GobikesApi.get(`/vendors/hubs/${selectedHub.id}/tickets`, {
          params: {
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
            ...(statusFilter && { status: statusFilter }),
            ...(priorityFilter && { priority: priorityFilter })
          },
          headers: { 'vendor-token': authCookie }
        });
        setTickets(data.data.rows);
        setTotalCount(data.data.count);
      } catch (e) {
        setError(parseErrorForAxios(e));
      }
      setLoading(false);
    })();
  }, [selectedHub, page, statusFilter, priorityFilter]);

  // Load ticket detail
  useEffect(() => {
    const authCookie = getAuthCookie();
    setDetail(null);
    if (!authCookie || !detailTicketId) return;
    setDetailLoading(true);
    (async () => {
      try {
        const { data } = await GobikesApi.get(`/vendors/hubs/${selectedHub.id}/tickets/${detailTicketId}`, { headers: { 'vendor-token': authCookie } });
        setDetail(data.data);
      } catch (e) {
        setError(parseErrorForAxios(e));
        setDetailTicketId(null);
      }
      setDetailLoading(false);
    })();
  }, [detailTicketId]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns: GridColumns = [
    { field: 'id', headerName: 'Ticket ID', width: 110, sortable: false, valueFormatter: (params) => `GOW-${params.value}` },
    { field: 'orderId', headerName: 'Order ID', width: 100, sortable: false },
    { field: 'category', headerName: 'Category', width: 240, sortable: false, valueFormatter: (params) => CATEGORY_LABELS[params.value as string] || params.value },
    { field: 'subReason', headerName: 'Sub-Reason', width: 220, sortable: false, valueFormatter: (params) => params.value || '-' },
    { field: 'priority', headerName: 'Priority', width: 110, sortable: false, valueFormatter: (params) => formatOption(PRIORITY_OPTIONS, params.value as string) },
    { field: 'status', headerName: 'Status', width: 120, sortable: false, valueFormatter: (params) => formatOption(STATUS_OPTIONS, params.value as string) },
    { field: 'createdAt', headerName: 'Created At', width: 200, sortable: false, valueFormatter: (params) => params.value ? formatDate(params.value as string) : '-' },
    {
      field: 'view',
      headerName: 'Timeline',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Button size='small' variant='outlined' onClick={() => setDetailTicketId(params.row.id)}>
          View
        </Button>
      )
    }
  ];

  // Return DOM
  return (
    <Box sx={{ mt: { xs: '50px', md: 0 }, ml: { xs: 0, md: `${props.drawerWidth}px` }, width: { xs: '100%', md: `calc(100% - ${props.drawerWidth}px)` }, height: '100%' }}>
      <div className='gbk-tabcontent'>

        {/* Header */}
        <Box className='tab-header' sx={{ display: { xs: 'none', md: 'block' } }}>

          {/* Icon */}
          <div className={`tab-header-fab`} >
            <SupportAgent fontSize="large" />
          </div>

          {/* Text */}
          <Typography>
            Support Tickets
          </Typography>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mt: '30px', px: isPhone ? '12px' : 0 }}>
          <Select
            size='small'
            displayEmpty
            value={statusFilter}
            onChange={(e) => { setPage(0); setStatusFilter(e.target.value); }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value=''>All Statuses</MenuItem>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
          <Select
            size='small'
            displayEmpty
            value={priorityFilter}
            onChange={(e) => { setPage(0); setPriorityFilter(e.target.value); }}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value=''>All Priorities</MenuItem>
            {PRIORITY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </Box>
        {error && (
          <Typography variant='body2' color='error' sx={{ mt: 1, px: isPhone ? '12px' : 0 }}>{error}</Typography>
        )}

        {/* Tickets table */}
        <Box sx={{ mt: 2, height: isPhone ? 'calc(100% - 120px)' : 'calc(100% - 200px)', width: '100%' }}>
          <DataGrid
            className='gbk-dg'
            disableColumnMenu={true}
            disableSelectionOnClick
            loading={loading}
            rows={tickets}
            columns={columns}
            paginationMode='server'
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            pageSize={PAGE_SIZE}
            rowCount={totalCount}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        </Box>

        {/* Ticket detail */}
        <Dialog open={!!detailTicketId} onClose={() => setDetailTicketId(null)} maxWidth='sm' fullWidth>
          <DialogTitle>Ticket #GOW-{detailTicketId}</DialogTitle>
          <DialogContent>
            {detail && (
              <Box sx={{ mb: 2 }}>
                <Typography variant='body2'>Order ID: {detail.ticket.orderId}</Typography>
                <Typography variant='body2'>Category: {CATEGORY_LABELS[detail.ticket.category] || detail.ticket.category}</Typography>
                {detail.ticket.subReason && <Typography variant='body2'>Sub-Reason: {detail.ticket.subReason}</Typography>}
                {detail.ticket.agentRemark && <Typography variant='body2'>Remark: {detail.ticket.agentRemark}</Typography>}
                <Typography variant='body2'>Priority: {formatOption(PRIORITY_OPTIONS, detail.ticket.priority)}</Typography>
                <Typography variant='body2'>Status: {formatOption(STATUS_OPTIONS, detail.ticket.status)}</Typography>
                <Typography variant='body2'>Created At: {formatDate(detail.ticket.createdAt)}</Typography>

                {detail.ticket.order?.user && (
                  <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant='subtitle2' sx={{ mb: 0.5 }}>Customer Details</Typography>
                    <Typography variant='body2'>Name: {`${detail.ticket.order.user.firstName} ${detail.ticket.order.user.lastName}`}</Typography>
                    <Typography variant='body2'>Phone: {detail.ticket.order.user.phone || '-'}</Typography>
                  </Box>
                )}
              </Box>
            )}

            <Typography variant='subtitle2' sx={{ mb: 1 }}>Timeline</Typography>
            <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
              {detailLoading && <Typography variant='body2'>Loading...</Typography>}
              {!detailLoading && detail && !detail.events.length && <Typography variant='body2' color='text.secondary'>No activity yet.</Typography>}
              {detail?.events.map((event) => (
                <Box key={event.id} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant='caption' color='text.secondary'>
                    {EVENT_TYPE_LABELS[event.type] || event.type} · {formatDate(event.createdAt)}
                  </Typography>
                  <Typography variant='body2'>{event.text}</Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailTicketId(null)}>Close</Button>
          </DialogActions>
        </Dialog>

      </div>
    </Box>
  )
}
